import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';
import { DialogTitle, DialogContentText, Button, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { FileUploadV2, ImporterProgress } from '~components/index';
import {
  useLazyRequest,
  useImporterCustomersProgressListener,
  useSampleTemplates,
} from '~hooks/index';
import { importVehiclesByCSV } from '~services/admin/vehicle';
import { AdminVehicleImporter } from '~services/admin/vehicle/types';
import { getImporterVehiclesFromCSVAlertDescription } from '~utils/vehicle';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { DialogImportVehiclesFromCSVProps } from './types';
import {
  DialogImportVehiclesFromCSVContainer,
  DialogImportVehiclesFromCSVContent,
  DialogImportVehiclesFromCSVActions,
} from './styles';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const defaultImportedCustomersData: AdminVehicleImporter = {
  errorRows: 0,
  importedRows: 0,
  message: '',
  totalRows: 0,
};

const DialogImportVehiclesFromCSV = ({
  open,
  onClose,
  onSubmitSuccess,
}: DialogImportVehiclesFromCSVProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const { vehicles: dowloadTemplateUrl } = useSampleTemplates();

  const [runImporterProgressListener, setImporterProgressListener] =
    useState(false);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [importedVehiclesData, setImportedVehiclesData] =
    useState<AdminVehicleImporter>(defaultImportedCustomersData);

  const {
    status,
    updateImporterStatus,
    data: { progress, step, total },
    reset: resetImporter,
  } = useImporterCustomersProgressListener({
    runListenerUpdates: open && runImporterProgressListener,
    onCompleted: () => {
      setImporterProgressListener(false);

      if (importedVehiclesData.errorRows < 1) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    },
  });

  const loading = useMemo(() => status === 'inProgress', [status]);

  const isImporting = useMemo(
    () => loading || status === 'completed' || runImporterProgressListener,
    [loading, status, runImporterProgressListener],
  );

  const [, , , executeImportCustomers] = useLazyRequest({
    request: importVehiclesByCSV,
    withPostSuccess: (response) => {
      const responseImporter = response.data?.data as AdminVehicleImporter;

      setImportedVehiclesData(responseImporter);
      setIsCompletedRequest(true);
      onSubmitSuccess();
    },
    withPostFailure: () => {
      setIsCompletedRequest(true);
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const isCompletedProcessImporter = useMemo(
    () => status === 'completed' && isCompletedRequest,
    [status, isCompletedRequest],
  );

  const importerProgressTitle = useMemo(
    () =>
      isCompletedProcessImporter
        ? 'Archivo importado'
        : 'Importando desde archivo',
    [isCompletedProcessImporter],
  );

  const importerProgressDescription = useMemo(
    () =>
      isCompletedProcessImporter
        ? getImporterVehiclesFromCSVAlertDescription(importedVehiclesData)
        : `Importando ${step} de ${total}`,
    [isCompletedProcessImporter, importedVehiclesData, step, total],
  );

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      setImporterProgressListener(true);
      updateImporterStatus('inProgress');
      setIsCompletedRequest(false);
      await executeImportCustomers(values[FIELDS_NAME.FILES]);

      setSubmitting(false);
    },
    [updateImporterStatus, executeImportCustomers],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { resetForm, submitForm, isSubmitting, dirty, isValid, values } =
    formikBag;

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetForm();
        resetImporter();
        setImporterProgressListener(false);
        setIsCompletedRequest(false);
        setImportedVehiclesData(defaultImportedCustomersData);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const disabledSubmit = useMemo(
    () => !dirty || !isValid || !values[FIELDS_NAME.FILES],
    [dirty, isValid, values],
  );

  return (
    <FormikProvider value={formikBag}>
      <DialogImportVehiclesFromCSVContainer
        open={open}
        onClose={onClose}
        maxWidth="sm"
      >
        <DialogTitle>Importar Vehículos</DialogTitle>

        <DialogImportVehiclesFromCSVContent dividers>
          {!isImporting && (
            <DialogContentText color="text.primary">
              Importar vehículos desde un archivo de forma masiva
            </DialogContentText>
          )}

          <FileUploadV2
            name={FIELDS_NAME.FILES}
            labelImg={{
              src: toAbsoluteUrl('/images/import_file.png'),
              alt: 'Import File',
            }}
            accept=".csv"
            disabled={isImporting}
          />

          {!isImporting && (
            <DialogContentText variant="caption">
              Si deseas importar de forma masiva utilizando un archivo, puedes{' '}
              <Link
                href={dowloadTemplateUrl}
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                descargar y modificar la plantilla de ejemplo.
              </Link>{' '}
              Recuerda respetar el orden de las columnas. Si necesitas
              información consulta la{' '}
              <Link
                href="https://routix.tawk.help" // @TODO: Verify and change
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                ayuda.
              </Link>{' '}
            </DialogContentText>
          )}

          {isImporting && (
            <ImporterProgress
              title={importerProgressTitle}
              description={importerProgressDescription}
              progress={progress}
              warning={importedVehiclesData.errorRows >= 1}
            />
          )}
        </DialogImportVehiclesFromCSVContent>

        <DialogImportVehiclesFromCSVActions>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {!isImporting && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={submitForm}
              loading={loading || isSubmitting}
              disabled={disabledSubmit}
            >
              Importar
            </LoadingButton>
          )}
        </DialogImportVehiclesFromCSVActions>
      </DialogImportVehiclesFromCSVContainer>
    </FormikProvider>
  );
};

export default DialogImportVehiclesFromCSV;
